if (!customElements.get('notification-subscriber')) {

  class NotificationSubscriber extends HTMLElement {
    constructor() {
      super();
      this.notifications = [];
      this.maxNotifications = 5;
      this.defaultDuration = 4000;
      this.zIndexCounter = 9999;
    }

    connectedCallback() {
      // Create container for notifications
      this.createContainer();
      
      // Listen for notification events
      document.addEventListener('notification', (event) => {
        const detail = event.detail;
        if (detail && detail.message) {
          this.showNotification(detail.message, detail.type || 'info', detail.duration, detail.product);
        }
      });
    }

    createContainer() {
      // Create a container if it doesn't exist
      if (!this.container) {
        this.container = document.createElement('div');
        this.container.className = 'notification-container';
        this.container.style.cssText = `
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 9999;
          pointer-events: none;
        `;
        document.body.appendChild(this.container);
      }
    }

    showNotification(message, type = 'info', duration, product) {
      const notificationDuration = duration || this.defaultDuration;
      
      // Create notification element
      const toast = this.createToast(message, type, product);
      
      // Add to container
      this.container.appendChild(toast);
      this.notifications.push(toast);
      
      // Push older notifications down the stack
      this.reorderNotifications();
      
      // Remove old notifications if exceeding max
      if (this.notifications.length > this.maxNotifications) {
        const oldToast = this.notifications.shift();
        this.removeToast(oldToast);
      }
      
      // Trigger entrance animation
      requestAnimationFrame(() => {
        const targetOpacity = toast.dataset.targetOpacity || 1;
        const targetScale = toast.dataset.scale || 1;
        toast.style.transform = `translate(-50%, 0) scale(${targetScale})`;
        toast.style.opacity = targetOpacity;
      });
      
      // Auto dismiss after duration
      setTimeout(() => {
        this.removeToast(toast);
      }, notificationDuration);
    }

    createToast(message, type, product) {
      const toast = document.createElement('div');
      toast.className = `notification-toast notification-${type}`;
      
      // Get type-specific styles
      const styles = this.getTypeStyles(type);
      
      // Increment z-index for stacking
      this.zIndexCounter++;
      const stackOffset = 0; // Latest notification always at top
      const opacity = 1; // Latest notification always fully opaque
      const scale = 1; // Latest notification always at normal size
      
      toast.style.cssText = `
        background: #000000;
        color: ${styles.color};
        border: 1px solid ${styles.color};
        border-radius: 0;
        padding: 16px 20px;
        min-width: 300px;
        max-width: 400px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        display: flex;
        align-items: center;
        gap: 12px;
        pointer-events: auto;
        cursor: pointer;
        position: absolute;
        top: ${stackOffset}px;
        left: 50%;
        transform: translate(-50%, -20px) scale(${scale});
        opacity: 0;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        z-index: ${this.zIndexCounter};
      `;
      
      // Store initial opacity for this toast
      toast.dataset.targetOpacity = opacity;
      toast.dataset.scale = scale;
      
      // Add icon
      const icon = document.createElement('div');
      icon.innerHTML = this.getIcon(type);
      icon.style.cssText = `
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      `;
      
      // Add message
      const messageEl = document.createElement('div');
      if (product) {
        const strongEl = document.createElement('strong');
        strongEl.textContent = product;
        strongEl.style.fontWeight = '700';
        messageEl.appendChild(strongEl);
        messageEl.appendChild(document.createTextNode(' ' + message));
      } else {
        messageEl.textContent = message;
      }
      messageEl.style.cssText = `
        flex: 1;
        font-size: 14px;
        line-height: 1.4;
        font-weight: 500;
        word-wrap: break-word;
        overflow-wrap: break-word;
        text-align: center;
      `;
      
      // Add close button
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = '×';
      closeBtn.style.cssText = `
        background: none;
        border: none;
        color: #9ca3af;
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.6;
        transition: opacity 0.2s;
      `;
      closeBtn.addEventListener('mouseenter', () => closeBtn.style.opacity = '1');
      closeBtn.addEventListener('mouseleave', () => closeBtn.style.opacity = '0.6');
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeToast(toast);
      });
      
      // Assemble toast
      toast.appendChild(icon);
      toast.appendChild(messageEl);
      toast.appendChild(closeBtn);
      
      // Click to dismiss
      toast.addEventListener('click', () => {
        this.removeToast(toast);
      });
      
      return toast;
    }

    removeToast(toast) {
      if (!toast || !toast.parentElement) return;
      
      // Exit animation
      toast.style.transform = 'translate(-50%, -20px)';
      toast.style.opacity = '0';
      
      setTimeout(() => {
        if (toast.parentElement) {
          toast.remove();
        }
        // Remove from notifications array
        const index = this.notifications.indexOf(toast);
        if (index > -1) {
          this.notifications.splice(index, 1);
        }
        
        // Animate remaining notifications to fill the gap
        this.reorderNotifications();
      }, 300);
    }

    reorderNotifications() {
      // Adjust stack positions, opacity, and scale for all toasts
      // Latest notification (last in array) stays at top, older ones move down
      const total = this.notifications.length;
      this.notifications.forEach((toast, index) => {
        if (toast && toast.parentElement) {
          // Calculate position from the end (0 for latest, increasing for older)
          const positionFromTop = total - 1 - index;
          const stackOffset = positionFromTop * 8;
          const opacity = Math.max(0.5, 1 - (positionFromTop * 0.1));
          const scale = Math.min(1.1, 1 + (positionFromTop * 0.02));
          toast.style.top = `${stackOffset}px`;
          toast.style.opacity = opacity;
          toast.style.transform = `translate(-50%, 0) scale(${scale})`;
          toast.dataset.targetOpacity = opacity;
        }
      });
    }

    getTypeStyles(type) {
      const styles = {
        success: {
          color: '#ffffff'
        },
        error: {
          color: '#ef4444'
        },
        warning: {
          color: '#f59e0b'
        },
        info: {
          color: '#3b82f6'
        }
      };
      
      return styles[type] || styles.info;
    }

    getIcon(type) {
      const icons = {
        success: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style="width: 20px; height: 20px;">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>`,
        error: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style="width: 20px; height: 20px;">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>`,
        warning: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style="width: 20px; height: 20px;">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>`,
        info: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style="width: 20px; height: 20px;">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>`
      };
      
      return icons[type] || icons.info;
    }

    disconnectedCallback() {
      // Clean up notifications
      this.notifications.forEach(toast => {
        if (toast.parentElement) {
          toast.remove();
        }
      });
      
      // Remove container
      if (this.container && this.container.parentElement) {
        this.container.remove();
      }
    }
  }

  customElements.define('notification-subscriber', NotificationSubscriber);
}